/* eslint-disable @nrwl/nx/enforce-module-boundaries */
import { fieldEqualTo } from '@ideapa/app-forms';
import { Ente } from '@ideapa/app-shared-ente';
import { Modulo } from '@ideapa/app-shared-modulo';
import { UserWithEntiDto } from '@ideapa/common-dto';
import { createReducer, on } from '@ngrx/store';
import moment from 'moment';
import {
  box,
  Boxed,
  createFormGroupState,
  FormGroupState,
  onNgrxForms,
  updateGroup,
  validate,
  wrapReducerWithFormStateUpdate,
} from 'ngrx-forms';
import { email, minLength, pattern, required } from 'ngrx-forms/validation';
import * as AuthActions from './auth.actions';

export const AUTH_FEATURE_KEY = 'auth';
export const LOGIN_FORM_ID = 'loginForm';
export const PROFILE_CHANGE_PASSWORD_FORM_ID = 'profileChangePasswordForm';
export const FORGOT_PASSWORD_FORM_ID = 'forgotPasswordForm';
export const RESET_PASSWORD_FORM_ID = 'resetPasswordForm';
export const SELECT_MODULO_FORM_ID = 'moduloForm';
export const SELECT_MODULO_PARTECIPATA_FORM_ID = 'moduloPartecipataForm';
export const CHANGE_PASSWORD_FORM_ID = 'changePasswordForm';
export const MA_VERIFY_CODE_FORM_ID = 'maVerifyCodeForm';
export const EMAIL_OTP_VERIFY_CODE_FORM_ID = 'emailOtpVerifyCodeForm';

export interface LoginFormValue {
  username: string;
  password: string;
  rememberMe: boolean;
  newPassword: string;
  confirmPassword: string;
}
export interface ForgotPasswordFormValue {
  username: string;
}
export interface ResetPasswordFormValue {
  newPassword: string;
  confirmPassword: string;
}
export interface ChangePasswordFormValue {
  password: string;
  newPassword: string;
  confirmPassword: string;
}
export interface SelectModuloFormValue {
  ente: Boxed<Ente | undefined>;
  anno: number;
}

export interface SelectModuloPartecipataFormValue {
  anno: number;
}

export interface MAVerifyCodeFormValue {
  otpCode: string;
}

export interface EmailOtpVerifyCodeFormValue {
  otpCode: string;
}
export interface AuthState {
  loginForm: FormGroupState<LoginFormValue>;
  loggedUser?: UserWithEntiDto;
  modulo: Modulo | null;
  ente: Ente | null;
  anno: number | null;
  forgotPasswordForm: FormGroupState<ForgotPasswordFormValue>;
  resetPasswordForm: FormGroupState<ResetPasswordFormValue>;
  selectModuloForm: FormGroupState<SelectModuloFormValue>;
  selectModuloPartecipataForm: FormGroupState<SelectModuloPartecipataFormValue>;
  changePasswordForm: FormGroupState<ChangePasswordFormValue>;
  authError: string;
  resetPasswordEmailSent: boolean;
  userMustResetPassword: boolean;
  MAVerifyCodeForm: FormGroupState<MAVerifyCodeFormValue>;
  EmailOtpVerifyCodeForm: FormGroupState<EmailOtpVerifyCodeFormValue>;
}

export interface AuthPartialState {
  readonly [AUTH_FEATURE_KEY]: AuthState;
}

export const loginFormInitialState: LoginFormValue = {
  username: '',
  password: '',
  rememberMe: false,
  newPassword: '',
  confirmPassword: '',
};

export const forgotPasswordFormInitialState: ForgotPasswordFormValue = {
  username: '',
};

export const resetPasswordFormInitialState: ResetPasswordFormValue = {
  newPassword: '',
  confirmPassword: '',
};

export const changePasswordFormInitialState: ChangePasswordFormValue = {
  password: '',
  newPassword: '',
  confirmPassword: '',
};

export const selectModuloFormInitialState: SelectModuloFormValue = {
  ente: box(undefined),
  anno: moment().subtract(1, 'y').year(),
};

export const selectModuloPartecipataFormInitialState: SelectModuloPartecipataFormValue =
  {
    anno: moment().subtract(1, 'y').year(),
  };

export const MAVerifyCodeFormInitialState: MAVerifyCodeFormValue = {
  otpCode: '',
};

export const emailOtpVerifyCodeFormInitialState: EmailOtpVerifyCodeFormValue = {
  otpCode: '',
};

export const initialState: AuthState = {
  // set initial required properties
  loginForm: createFormGroupState<LoginFormValue>(
    LOGIN_FORM_ID,
    loginFormInitialState
  ),
  loggedUser: undefined,
  userMustResetPassword: false,
  resetPasswordEmailSent: false,
  forgotPasswordForm: createFormGroupState<ForgotPasswordFormValue>(
    FORGOT_PASSWORD_FORM_ID,
    forgotPasswordFormInitialState
  ),
  resetPasswordForm: createFormGroupState<ResetPasswordFormValue>(
    FORGOT_PASSWORD_FORM_ID,
    resetPasswordFormInitialState
  ),
  selectModuloForm: createFormGroupState<SelectModuloFormValue>(
    SELECT_MODULO_FORM_ID,
    selectModuloFormInitialState
  ),
  selectModuloPartecipataForm:
    createFormGroupState<SelectModuloPartecipataFormValue>(
      SELECT_MODULO_PARTECIPATA_FORM_ID,
      selectModuloPartecipataFormInitialState
    ),
  changePasswordForm: createFormGroupState<ChangePasswordFormValue>(
    CHANGE_PASSWORD_FORM_ID,
    changePasswordFormInitialState
  ),
  authError: '',
  modulo: null,
  anno: null,
  ente: null,
  // loginResponse: null,
  MAVerifyCodeForm: createFormGroupState<MAVerifyCodeFormValue>(
    MA_VERIFY_CODE_FORM_ID,
    MAVerifyCodeFormInitialState
  ),
  EmailOtpVerifyCodeForm: createFormGroupState<EmailOtpVerifyCodeFormValue>(
    EMAIL_OTP_VERIFY_CODE_FORM_ID,
    emailOtpVerifyCodeFormInitialState
  ),
};

const validateLoginForm = (
  formState: FormGroupState<LoginFormValue>,
  state: AuthState
) => {
  return updateGroup(formState, {
    username: (controlState, parentState) => {
      controlState = validate(controlState, [required, email]);
      return controlState;
    },
    password: (controlState, parentState) => {
      controlState = validate(controlState, required);
      return controlState;
    },
    newPassword: (controlState, parentState) => {
      if (state.userMustResetPassword) {
        controlState = validate(controlState, [required, minLength(8)]);

        if (controlState.isValid) {
          controlState = validate(
            controlState,
            pattern(
              /^(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.*[!@#$%^&*_-])(?=.{8,})/
            )
          );
        }
      }
      return controlState;
    },
    confirmPassword: (controlState, parentState) => {
      if (state.userMustResetPassword) {
        controlState = validate(
          controlState,
          fieldEqualTo(
            parentState.value.newPassword,
            'Conferma password',
            'Nuova Password'
          )
        );
      }
      return controlState;
    },
  });
};

const validateForgotPasswordForm = updateGroup<ForgotPasswordFormValue>({
  username: validate(required, email),
});

const validateSelectModuloForm = updateGroup<SelectModuloFormValue>({
  anno: validate(required),
  ente: validate(required),
});

const validateSelectModuloPartecipataForm =
  updateGroup<SelectModuloPartecipataFormValue>({
    anno: validate(required),
  });

const validateChangePasswordForm = updateGroup<ChangePasswordFormValue>({
  password: validate(required),
  newPassword: (controlState, parentState) => {
    controlState = validate(controlState, [required, minLength(8)]);

    if (controlState.isValid) {
      controlState = validate(
        controlState,
        pattern(/^(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.*[!@#$%^&*_-])(?=.{8,})/)
      );
    }

    return controlState;
  },
  confirmPassword: (controlState, parentState) => {
    controlState = validate(
      controlState,
      fieldEqualTo(
        parentState.value.newPassword,
        'Conferma password',
        'Nuova Password'
      )
    );
    return controlState;
  },
});

const validateResetPasswordForm = (
  formState: FormGroupState<ResetPasswordFormValue>
) => {
  return updateGroup(formState, {
    newPassword: (controlState, parentState) => {
      controlState = validate(controlState, [required, minLength(8)]);

      if (controlState.isValid) {
        controlState = validate(
          controlState,
          pattern(
            /^(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.*[!@#$%^&*_-])(?=.{8,})/
          )
        );
      }
      return controlState;
    },
    confirmPassword: (controlState, parentState) => {
      controlState = validate(
        controlState,
        fieldEqualTo(
          parentState.value.newPassword,
          'Conferma password',
          'Nuova Password'
        )
      );
      return controlState;
    },
  });
};

const validateMAVerifyCodeForm = updateGroup<MAVerifyCodeFormValue>({
  otpCode: validate([required, minLength(6)]),
});
const validateEmailOtpVerifyCodeForm = updateGroup<EmailOtpVerifyCodeFormValue>(
  {
    otpCode: validate([required, minLength(6)]),
  }
);

const rawReducer = createReducer(
  initialState,
  onNgrxForms(),
  on(AuthActions.login, (state) => {
    return {
      ...state,
      ente: null,
      anno: null,
      modulo: null,
    };
  }),
  on(AuthActions.getLoggedUserSuccess, (state, { loggedUser }) => {
    return {
      ...state,
      loggedUser,
    };
  }),
  on(AuthActions.logout, (state) => {
    return {
      ...state,
      loginForm: createFormGroupState<LoginFormValue>(
        LOGIN_FORM_ID,
        loginFormInitialState
      ),
      loggedUser: undefined,
      modulo: null,
      ente: null,
      anno: null,
    };
  }),
  on(AuthActions.setUserMustResetPassword, (state) => {
    return { ...state, userMustResetPassword: true };
  }),
  on(AuthActions.loginSuccess, (state) => {
    return {
      ...state,
      userMustResetPassword: false,
      authError: '',
    };
  }),
  on(AuthActions.loginFailure, (state, { error }) => {
    return {
      ...state,
      authError: error,
    };
  }),
  // on(AuthActions.forgotPasswordSuccess, (state) => {
  //   return { ...state, resetPasswordEmailSent: true };
  // }),
  on(AuthActions.resetPasswordSuccess, (state) => {
    return { ...state, authError: '' };
  }),
  // on(AuthActions.resetPasswordFailure, (state, { error }) => {
  //   return { ...state, authError: error };
  // }),
  on(AuthActions.resetAuthError, (state) => {
    return { ...state, authError: '' };
  }),
  on(AuthActions.selectModulo, (state, { modulo }) => {
    return { ...state, modulo };
  }),
  on(AuthActions.clearModulo, (state) => {
    return {
      ...state,
      modulo: null,
      ente: null,
      anno: null,
      selectModuloForm: createFormGroupState(
        SELECT_MODULO_FORM_ID,
        selectModuloFormInitialState
      ),
      selectModuloPartecipataForm: createFormGroupState(
        SELECT_MODULO_PARTECIPATA_FORM_ID,
        selectModuloPartecipataFormInitialState
      ),
    };
  }),
  on(AuthActions.selectEnte, (state, { ente }) => {
    return { ...state, ente };
  }),
  on(AuthActions.selectAnno, (state, { anno }) => {
    return { ...state, anno };
  }),
  on(AuthActions.insertMAOTP, (state, { result }) => {
    return {
      ...state,
      MAVerifyCodeForm: createFormGroupState(
        MA_VERIFY_CODE_FORM_ID,
        MAVerifyCodeFormInitialState
      ),
    };
  }),
  on(AuthActions.insertEmailOTP, (state, { result }) => {
    return {
      ...state,
      EmailOtpVerifyCodeForm: createFormGroupState(
        EMAIL_OTP_VERIFY_CODE_FORM_ID,
        emailOtpVerifyCodeFormInitialState
      ),
    };
  })
);

export const reducer = wrapReducerWithFormStateUpdate(
  wrapReducerWithFormStateUpdate(
    wrapReducerWithFormStateUpdate(
      wrapReducerWithFormStateUpdate(
        wrapReducerWithFormStateUpdate(
          wrapReducerWithFormStateUpdate(
            wrapReducerWithFormStateUpdate(
              wrapReducerWithFormStateUpdate(
                rawReducer,
                // point to the form state to update
                (s) => s.MAVerifyCodeForm,
                // this function is always called after the reducer
                validateMAVerifyCodeForm
              ),
              (s) => s.EmailOtpVerifyCodeForm,
              // this function is always called after the reducer
              validateEmailOtpVerifyCodeForm
            ),
            // point to the form state to update
            (s) => s.resetPasswordForm,
            // this function is always called after the reducer
            validateResetPasswordForm
          ),
          // point to the form state to update
          (s) => s.forgotPasswordForm,
          // this function is always called after the reducer
          validateForgotPasswordForm
        ),
        // point to the form state to update
        (s) => s.loginForm,
        // this function is always called after the reducer
        validateLoginForm
      ),
      (s) => s.selectModuloForm,
      // this function is always called after the reducer
      validateSelectModuloForm
    ),
    (s) => s.selectModuloPartecipataForm,
    // this function is always called after the reducer
    validateSelectModuloPartecipataForm
  ),
  (s) => s.changePasswordForm,
  // this function is always called after the reducer
  validateChangePasswordForm
);
